import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';

// Swapped for the real (translated) label in transform() - the highlight callback below runs on
// a module-level MarkdownIt singleton with no access to a particular pipe instance's injected
// TranslateService.
const COPY_LABEL_PLACEHOLDER = '__ai_code_copy_label__';

// Module-level singleton: MarkdownIt/highlight.js configuration has no per-locale or per-instance
// state, so there's no reason to reconstruct it on every pipe invocation.
const md = new MarkdownIt({
  // DOMPurify.sanitize() below is the real security boundary, not this flag - it runs
  // unconditionally on the rendered output and strips anything actually dangerous (script tags,
  // on* handlers, iframes) regardless. With html:false, markdown-it instead escapes ALL raw HTML
  // in the source to literal text - including benign formatting LLMs commonly emit inside table
  // cells (markdown has no native multi-line-cell syntax, so <br> is the common workaround),
  // which rendered as literal "<br>" text instead of a line break. html:true lets that safe
  // markup through to DOMPurify's allowlist instead of pre-escaping it.
  html: true,
  linkify: true,
  breaks: true,
  highlight: (code: string, lang: string): string => {
    const language = lang && hljs.getLanguage(lang) ? lang : undefined;
    let highlighted: string;
    try {
      highlighted = language ? hljs.highlight(code, { language }).value : md.utils.escapeHtml(code);
    } catch {
      highlighted = md.utils.escapeHtml(code);
    }
    // Plain markup, no Angular bindings - this HTML is injected via [innerHTML], not compiled by
    // Angular. chat-item.component.ts's onCodeBlockClick() delegates click handling and reads
    // the sibling <code>'s text directly from the DOM rather than needing any state here.
    return (
      '<pre class="ai-code-block"><button type="button" class="ai-code-copy-btn" data-code-copy>' +
      `<i class="mi2-copy" aria-hidden="true"></i><span class="ai-code-copy-label">${COPY_LABEL_PLACEHOLDER}</span></button>` +
      `<code class="hljs${language ? ' language-' + language : ''}">${highlighted}</code></pre>`
    );
  }
});

// markdown-it renders GFM pipe-tables natively (no plugin needed), but a wide table would
// otherwise overflow the message bubble - wrap it in its own horizontally-scrollable container
// rather than letting it push the whole bubble wider or clip.
md.renderer.rules.table_open = () => '<div class="table-wrapper"><table>';
md.renderer.rules.table_close = () => '</table></div>';

@Pipe({
  name: 'markdown',
  standalone: false
})
export class MarkdownPipe implements PipeTransform {
  constructor(
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return '';
    }
    const copyLabel = DOMPurify.sanitize(this.translate.instant('cl_chat.copyCode'), { ALLOWED_TAGS: [] });
    const rendered = md.render(value).split(COPY_LABEL_PLACEHOLDER).join(copyLabel);
    const sanitized = DOMPurify.sanitize(rendered, { ADD_ATTR: ['data-code-copy'] });
    // Already sanitized above via DOMPurify - bypassSecurityTrustHtml so Angular's own [innerHTML]
    // sanitizer doesn't run a second pass (which strips data-code-copy/aria-* and spams the console
    // with "sanitizing HTML stripped some content" on every streamed chunk).
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }
}
